import React, { useState } from 'react';
import { Brain, School, Sparkles, ArrowRight, Users, Calendar, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

function WelcomePage() {
  useDocumentTitle('Welcome');
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const handleEarlyAccessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Integrate with email service/waitlist
    console.log('Early access email submitted:', email);

    // Track event (will be implemented with analytics)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'early_access_submit', {
        email: email
      });
    }

    setEmailSubmitted(true);
    setEmail('');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark">
      {/* Navigation */}
      <nav className="border-b border-border-light bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-text-primary">Campus Connect</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-text-secondary hover:text-primary-500 transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-text-secondary hover:text-primary-500 transition-colors">
                Pricing
              </a>
              <Link to="/login" className="text-text-secondary hover:text-primary-500 transition-colors">
                Login
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div id="features" className="container mx-auto px-4 py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Left Column - Content */}
          <div className="flex-1 text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-6">
              <Brain className="w-10 h-10 text-primary-500" />
              <h1 className="text-3xl font-bold text-text-primary">Campus Connect</h1>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Cut Campus Admin Work by{' '}
              <span className="text-primary-500">50%</span>{' '}
              with AI
            </h2>

            <p className="text-xl text-text-secondary mb-4 max-w-2xl font-medium">
              CampusConnect centralizes communication, scheduling, and routine tasks so your staff can focus on students—not spreadsheets.
            </p>

            <p className="text-base text-text-muted mb-8 max-w-2xl">
              AI-powered campus management for K-12 and small colleges in one simple dashboard.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Link
                to="/signup"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg
                         flex items-center justify-center gap-2 transition-colors"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).gtag) {
                    (window as any).gtag('event', 'click', {
                      event_category: 'CTA',
                      event_label: 'Sign Up Free'
                    });
                  }
                }}
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/login"
                className="px-6 py-3 border-2 border-primary-500 text-primary-500
                         hover:bg-primary-50 rounded-lg transition-colors"
              >
                Login
              </Link>
            </div>

            {/* Early Access Email Capture */}
            <div className="border-2 border-primary-100 bg-gradient-to-br from-primary-50/30 to-secondary-50/30
                          p-6 rounded-xl max-w-md backdrop-blur-sm">
              <h3 className="text-base font-bold text-text-primary mb-1.5">Join the Early Access List</h3>
              <p className="text-sm text-text-secondary mb-4">
                Get CampusConnect free for your first 30 days, plus onboarding help.
              </p>
              {!emailSubmitted ? (
                <form onSubmit={handleEarlyAccessSubmit} className="flex flex-col sm:flex-row gap-2.5">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="flex-1 px-4 py-2.5 border-2 border-border-light bg-white rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                             text-sm transition-all"
                  />
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600
                             hover:from-primary-600 hover:to-primary-700
                             text-white rounded-lg transition-all shadow-sm hover:shadow-md
                             whitespace-nowrap font-medium text-sm"
                  >
                    Join Early Access
                  </button>
                </form>
              ) : (
                <div className="flex items-center gap-2.5 text-success-600 bg-success-50 p-3 rounded-lg">
                  <Check className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Thanks! We'll be in touch soon.</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column - Feature Cards */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <FeatureCard
              icon={<School className="w-6 h-6 text-secondary-500" />}
              title="All Your Systems in One View"
              description="Syncs with Google Classroom, Canvas, and more so staff never double-enter data."
            />
            <FeatureCard
              icon={<Sparkles className="w-6 h-6 text-accent-500" />}
              title="Automate Repetitive Requests"
              description="Auto-route attendance issues, parent requests, and approval workflows."
            />
            <FeatureCard
              icon={<Users className="w-6 h-6 text-secondary-500" />}
              title="One Communication Hub"
              description="Announcements, chat, and alerts in one place for admins, teachers, and staff."
            />
            <FeatureCard
              icon={<Calendar className="w-6 h-6 text-accent-500" />}
              title="No More Calendar Chaos"
              description="Smart reminders for events, meetings, and compliance deadlines."
            />
          </div>
        </div>
        
        {/* Trust Banner */}
        <div className="mt-16 py-8 px-6 bg-surface-light rounded-xl shadow-lg">
          <div className="text-center">
            <p className="text-base text-text-primary font-semibold mb-4">Built for K-12 schools, academies, and small colleges.</p>
            <p className="text-sm text-text-muted mb-4">TRUSTED BY LEADING EDUCATIONAL INSTITUTIONS</p>
            <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
              <div className="text-text-secondary font-semibold">Christian K-12 school</div>
              <div className="text-text-secondary font-semibold">Private academy</div>
              <div className="text-text-secondary font-semibold">Small private college</div>
              <div className="text-text-secondary font-semibold">Charter school</div>
            </div>
            <div className="mt-6 pt-6 border-t border-border-light">
              <p className="text-base text-text-secondary italic">
                "CampusConnect saves our office 5+ hours a week."
              </p>
              <p className="text-sm text-text-muted mt-2">— Administrative Director, Private K-12 School</p>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing" className="mt-16 py-12 px-6 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl shadow-lg">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-text-primary mb-4">Simple, Transparent Pricing</h3>
            <p className="text-base text-text-secondary">Start free for 14 days. No credit card required.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="text-center">
                <h4 className="text-xl font-bold text-text-primary mb-2">Starter</h4>
                <p className="text-sm text-text-muted mb-4">Up to 500 students</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-primary-500">$49</span>
                  <span className="text-text-muted">/month</span>
                </div>
                <ul className="text-left space-y-2 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Basic LMS integration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">AI-powered automation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Team collaboration tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Email support</span>
                  </li>
                </ul>
                <Link
                  to="/signup"
                  className="block w-full px-4 py-2 border-2 border-primary-500 text-primary-500
                           hover:bg-primary-50 rounded-lg transition-colors text-center"
                >
                  Start Free Trial
                </Link>
              </div>
            </div>

            {/* Growth Plan */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-2xl transition-shadow border-2 border-primary-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              <div className="text-center">
                <h4 className="text-xl font-bold text-text-primary mb-2">Growth</h4>
                <p className="text-sm text-text-muted mb-4">Up to 2,000 students</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-primary-500">$99</span>
                  <span className="text-text-muted">/month</span>
                </div>
                <ul className="text-left space-y-2 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Everything in Starter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Advanced analytics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Custom workflows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Priority support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">API access</span>
                  </li>
                </ul>
                <Link
                  to="/signup"
                  className="block w-full px-4 py-2 bg-primary-500 hover:bg-primary-600
                           text-white rounded-lg transition-colors text-center"
                >
                  Start Free Trial
                </Link>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
              <div className="text-center">
                <h4 className="text-xl font-bold text-text-primary mb-2">Enterprise</h4>
                <p className="text-sm text-text-muted mb-4">Districts & colleges</p>
                <div className="mb-6">
                  <span className="text-2xl font-bold text-primary-500">Custom</span>
                </div>
                <ul className="text-left space-y-2 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Everything in Growth</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Unlimited students</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Dedicated support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">Custom integrations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">On-site training</span>
                  </li>
                </ul>
                <a
                  href="mailto:contact@campusconnectedu.com"
                  className="block w-full px-4 py-2 border-2 border-primary-500 text-primary-500
                           hover:bg-primary-50 rounded-lg transition-colors text-center"
                >
                  Talk to Us
                </a>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-text-muted italic">
              Founding schools get locked-in early pricing. Join the early access list above.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 py-8 border-t border-border-light">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary-500" />
              <span className="text-sm font-semibold text-text-primary">Campus Connect</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-text-muted">
              <Link to="/getting-started" className="hover:text-primary-500 transition-colors">Getting Started</Link>
              <Link to="/support" className="hover:text-primary-500 transition-colors">Support</Link>
              <Link to="/privacy" className="hover:text-primary-500 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-primary-500 transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
}) {
  return (
    <div className="p-6 bg-surface-light rounded-lg shadow-md hover:shadow-lg transition-shadow">
      <div className="mb-4">{icon}</div>
      <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
      <p className="text-text-secondary text-sm">{description}</p>
    </div>
  );
}

export default WelcomePage;